/**
 * Encryption/Decryption Module
 *
 * Handles AES-256-CBC encryption and decryption for secure API communication.
 * Uses CryptoJS library with MD5-hashed keys and a fixed zero IV.
 *
 * SECURITY NOTE: The IV is a static all-zeros vector. For stronger security,
 * consider generating a random IV per encryption and prepending it to the ciphertext.
 */
import CryptoJS from 'crypto-js';

// 16-byte Initialization Vector (all zeros) - must match PHP backend
const IV = CryptoJS.enc.Utf8.parse(
	'\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00',
);

/**
 * Generates an MD5 hash of the input string and returns it as a CryptoJS WordArray.
 * Used to derive a consistent 32-character key from variable-length key strings.
 * @param data - The key string to hash
 * @returns CryptoJS WordArray suitable for use as an AES key
 */
export const hashMD5 = (data: string) => {
	const md5Key = CryptoJS.MD5(data).toString();
	return CryptoJS.enc.Utf8.parse(md5Key);
};

/**
 * Encrypts a JavaScript object using AES-CBC with PKCS7 padding.
 * The object is JSON-stringified before encryption.
 * @param data - The object to encrypt
 * @param key - The encryption key string (will be MD5-hashed internally)
 * @returns Hex-encoded encrypted string
 */
export const encryptData = (data: object, key: string): string => {
	const KEY = hashMD5(key);
	const jsonString = JSON.stringify(data);
	const encrypted = CryptoJS.AES.encrypt(jsonString, KEY, {
		iv: IV,
		mode: CryptoJS.mode.CBC,
		padding: CryptoJS.pad.Pkcs7,
	});
	// Convert Base64 output to Hex for transport
	return CryptoJS.enc.Hex.stringify(CryptoJS.enc.Base64.parse(encrypted.toString()));
};

/**
 * Decrypts a Hex-encoded AES-CBC encrypted string back to a JavaScript object.
 * @param encryptedHex - Hex-encoded ciphertext received from the API
 * @param key - The decryption key string (must match the encryption key)
 * @returns Parsed JSON object, or null if decryption fails
 */
export const decryptData = (encryptedHex: string, key: string) => {
	if (!encryptedHex || typeof encryptedHex !== 'string') {
		// SECURITY FIX: Only log decryption errors in development
		if (process.env.NODE_ENV === 'development') {
			console.error('decryptData: Invalid encryptedHex parameter:', encryptedHex);
		}
		return null;
	}

	try {
		const KEY = hashMD5(key);
		// Convert Hex back to Base64 for CryptoJS decryption
		const encryptedBase64 = CryptoJS.enc.Hex.parse(encryptedHex).toString(CryptoJS.enc.Base64);

		const decrypted = CryptoJS.AES.decrypt(encryptedBase64, KEY, {
			iv: IV,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		});

		const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

		if (!decryptedText) {
			if (process.env.NODE_ENV === 'development') {
				console.error('decryptData: Failed to decrypt data');
			}
			return null;
		}

		return JSON.parse(decryptedText);
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.error('decryptData: Error during decryption:', error);
		}
		return null;
	}
};
